/**
 * BookPropertyCheck — 3-screen booking wizard.
 *
 * Mounted at /property-check/book. Holds wizard state in a useReducer so the
 * 3 screens can share data without prop drilling. Inline lightweight signup
 * runs on Screen 3 if the user isn't authed; users who land already authed
 * skip straight to payment.
 *
 * On successful submission:
 *   - If paymentMethod === 'manual_test' (dev) — request is created paid,
 *     matching kicks off, and we render <VerifierMatchingStatus />
 *   - If paymentMethod === 'stripe_checkout' — request is created in
 *     pending_payment, and we redirect to the Stripe Checkout URL the
 *     backend returned
 */

import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Alert,
  Divider,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Chip,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../contexts/AuthContext';
import propertyCheckService from '../services/propertyCheckService';
import ownerPropertiesService from '../services/ownerPropertiesService';
import PackageCard from '../components/property-check/PackageCard';
import PropertyAccessForm from '../components/property-check/PropertyAccessForm';
import FocusAreaChips from '../components/property-check/FocusAreaChips';
import LightweightSignupInline from '../components/property-check/LightweightSignupInline';
import VerifierMatchingStatus from '../components/property-check/VerifierMatchingStatus';
import LocationInput from '../components/common/LocationInput';
import AIDescriptionField from '../components/common/AIDescriptionField';
import placesService from '../services/placesService';
import { colors, brand, text as textColors } from '../theme/colors';
import { useFeatures } from '../contexts/FeaturesContext';
import Seo from '../components/Seo';
import { DarkCard, LightCard, INK } from '../components/directory/DirectoryLayout';

// Mirror the backend getLocationCoordinates: pull lat/lng out of whatever shape
// LocationInput emitted (Google place, GPS pin, manual coordinates).
const readCoordinates = (location) => {
  if (!location || typeof location !== 'object') return null;
  const nested = location.coordinates || location.gpsLocation || {};
  const lat = location.latitude ?? location.lat ?? nested.latitude ?? nested.lat;
  const lng =
    location.longitude ?? location.lng ?? location.lon ??
    nested.longitude ?? nested.lng ?? nested.lon;
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;
  return { lat: latNum, lng: lngNum };
};

// Same convention as UnifiedProjectWizard / CreateVerificationRequest so the
// "Location *" labels render identically across the app.
const RequiredMark = () => (
  <Box component="span" sx={{ color: 'error.main' }}>*</Box>
);

const STEPS = ['Your check', 'Property access', 'Pay & book'];

const initialState = {
  step: 0,
  packageSlug: null,
  pkg: null,
  location: null,
  description: '',
  ownerPropertyId: null,
  scheduling: {
    requestedDate: '',
    preferredStartTime: '',
    preferredEndTime: '',
    flexibility: 'flexible',
  },
  propertyAccess: {},
  focusAreas: [],
  // Pay when the report arrives is the default: no card before anyone has
  // visited (the only real booking we ever had stopped at the card step).
  paymentMethod: process.env.NODE_ENV === 'production' ? 'pay_on_report' : 'manual_test',
  selectionMode: 'auto_assign',
  requestedVerifierId: '',
};

const BOOKING_DRAFT_KEY = 'eqaya_property_check_booking_draft';
const POST_VERIFY_REDIRECT_KEY = 'eqaya_property_check_post_verify_redirect';

const rememberPostVerifyReturn = () => {
  const target = JSON.stringify({
    pathname: '/property-check/book',
    search: '?resume=1',
  });
  sessionStorage.setItem(POST_VERIFY_REDIRECT_KEY, target);
  localStorage.setItem(POST_VERIFY_REDIRECT_KEY, target);
};

// Drafts older than this are silently discarded: resuming this morning's
// half-typed booking is helpful; resurrecting last week's looks like a bug
// ("Book a Check only opens my old one").
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const clearDraftStorage = () => {
  sessionStorage.removeItem(BOOKING_DRAFT_KEY);
  localStorage.removeItem(BOOKING_DRAFT_KEY);
};

const loadDraftState = () => {
  try {
    const raw = sessionStorage.getItem(BOOKING_DRAFT_KEY) || localStorage.getItem(BOOKING_DRAFT_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    if (!parsed.savedAt || Date.now() - parsed.savedAt > DRAFT_MAX_AGE_MS) {
      clearDraftStorage();
      return initialState;
    }
    return {
      ...initialState,
      ...parsed,
      scheduling: { ...initialState.scheduling, ...(parsed.scheduling || {}) },
      propertyAccess: parsed.propertyAccess || initialState.propertyAccess,
      focusAreas: Array.isArray(parsed.focusAreas) ? parsed.focusAreas : initialState.focusAreas,
      selectionMode: parsed.selectionMode || initialState.selectionMode,
      requestedVerifierId: parsed.requestedVerifierId || initialState.requestedVerifierId,
    };
  } catch (_) {
    return initialState;
  }
};

function reducer(state, action) {
  switch (action.type) {
    case 'set':
      return { ...state, ...action.payload };
    case 'setStep':
      return { ...state, step: action.payload };
    case 'reset':
      return initialState;
    default:
      return state;
  }
}

const BookPropertyCheck = () => {
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const initialPackage = searchParams.get('package');
  const initialPropertyId = searchParams.get('propertyId');

  const [state, dispatch] = useReducer(reducer, initialState, loadDraftState);
  // True when we restored a meaningful unfinished booking: show a banner so
  // the user knows why the form isn't blank — and give them a way out. A
  // silently-resumed draft at the pay step reads as "it won't start a new one".
  const [resumedDraft, setResumedDraft] = useState(
    () => Boolean(state.step > 0 || state.packageSlug || state.location)
  );
  const startFresh = () => {
    clearDraftStorage();
    dispatch({ type: 'reset' });
    setResumedDraft(false);
  };
  const [packages, setPackages] = useState([]);
  const [ownerProperties, setOwnerProperties] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { propertyCheck, checkoutUrl }
  const [previousVerifiers, setPreviousVerifiers] = useState([]);
  const [availableVerifiers, setAvailableVerifiers] = useState([]);
  const [loadingVerifiers, setLoadingVerifiers] = useState(false);

  const authProvider = user?.authProvider || user?.auth_provider || 'local';
  const emailVerified = !user || authProvider !== 'local' || user.emailVerified || user.email_verified;

  useEffect(() => {
    const draft = JSON.stringify({ ...state, savedAt: Date.now() });
    sessionStorage.setItem(BOOKING_DRAFT_KEY, draft);
    localStorage.setItem(BOOKING_DRAFT_KEY, draft);
  }, [state]);

  // Load packages on mount; if URL param matches a package, preselect it.
  useEffect(() => {
    (async () => {
      try {
        const pkgs = await propertyCheckService.listPackages();
        setPackages(pkgs);
        if (initialPackage) {
          const match = pkgs.find((p) => p.slug === initialPackage);
          if (match) dispatch({ type: 'set', payload: { packageSlug: match.slug, pkg: match } });
        }
      } catch (err) {
        setError(err?.response?.data?.error || err.message);
      }
    })();
  }, [initialPackage]);

  // Load saved properties if the user is authed
  useEffect(() => {
    if (!user) {
      setOwnerProperties([]);
      return;
    }
    ownerPropertiesService.list().then(setOwnerProperties).catch(() => setOwnerProperties([]));
  }, [user]);

  useEffect(() => {
    if (!initialPropertyId || ownerProperties.length === 0 || state.ownerPropertyId) return;
    const prop = ownerProperties.find((p) => p.id === initialPropertyId);
    if (!prop) return;
    dispatch({
      type: 'set',
      payload: {
        ownerPropertyId: prop.id,
        location: prop.location || state.location,
        propertyAccess: prop.defaultAccessInfo || state.propertyAccess,
        focusAreas:
          prop.defaultFocusAreas && prop.defaultFocusAreas.length > 0
            ? prop.defaultFocusAreas
            : state.focusAreas,
      },
    });
  }, [initialPropertyId, ownerProperties, state.ownerPropertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || state.step !== 2) return;
    let cancelled = false;
    setLoadingVerifiers(true);
    Promise.all([
      propertyCheckService.listPreviousVerifiers().catch(() => []),
      propertyCheckService.listAvailableVerifiers().catch(() => []),
    ])
      .then(([previous, available]) => {
        if (cancelled) return;
        setPreviousVerifiers(previous);
        setAvailableVerifiers(available);
      })
      .finally(() => {
        if (!cancelled) setLoadingVerifiers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, state.step]);

  const canAdvanceFromScreen1 = !!(state.packageSlug && state.location);

  const locationRef = useRef(null);

  const handleSelectPackage = (pkg) => {
    dispatch({ type: 'set', payload: { packageSlug: pkg.slug, pkg } });
    // Selecting a package advances the visitor to the location/details stage.
    setTimeout(() => {
      locationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const handleSelectOwnerProperty = (id) => {
    if (!id) {
      dispatch({ type: 'set', payload: { ownerPropertyId: null } });
      return;
    }
    const prop = ownerProperties.find((p) => p.id === id);
    if (!prop) return;
    dispatch({
      type: 'set',
      payload: {
        ownerPropertyId: prop.id,
        location: prop.location || state.location,
        propertyAccess: prop.defaultAccessInfo || state.propertyAccess,
        focusAreas:
          prop.defaultFocusAreas && prop.defaultFocusAreas.length > 0
            ? prop.defaultFocusAreas
            : state.focusAreas,
      },
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      // A verifier can only be sent to a physical spot, so the booking gate
      // requires coordinates. If the owner typed an address but never picked a
      // Google suggestion (so no pin was captured), geocode the address to
      // derive a pin. The address stays primary — we only attach coordinates.
      let location = state.location;
      if (location && !readCoordinates(location)) {
        const addressText =
          location.address || location.formattedAddress ||
          (typeof location === 'string' ? location : null);
        if (addressText) {
          try {
            const geo = await placesService.geocodeAddress(addressText);
            if (geo?.success && geo.data?.coordinates) {
              const base = typeof location === 'string' ? { address: location } : location;
              location = { ...base, coordinates: geo.data.coordinates };
            }
          } catch (geoErr) {
            // Fall through — the backend gate will surface a clear error below.
          }
        }
      }
      const payload = {
        packageSlug: state.packageSlug,
        location,
        ownerPropertyId: state.ownerPropertyId || undefined,
        propertyAccess:
          state.propertyAccess && Object.keys(state.propertyAccess).length > 0
            ? state.propertyAccess
            : undefined,
        focusAreas: state.focusAreas.length > 0 ? state.focusAreas : undefined,
        description: state.description?.trim() ? state.description.trim() : undefined,
        scheduling: state.scheduling,
        paymentMethod: state.paymentMethod,
        selectionMode: state.selectionMode !== 'auto_assign' ? 'direct_verifier' : 'auto_assign',
        requestedVerifierId:
          state.selectionMode !== 'auto_assign' ? state.requestedVerifierId : undefined,
      };
      if (state.paymentMethod === 'stripe_checkout') {
        payload.successUrl = `${window.location.origin}/property-check/payment-success?session_id={CHECKOUT_SESSION_ID}`;
        payload.cancelUrl = `${window.location.origin}/property-check/book?cancelled=1`;
      }
      const res = await propertyCheckService.create(payload);
      sessionStorage.removeItem(BOOKING_DRAFT_KEY);
      localStorage.removeItem(BOOKING_DRAFT_KEY);
      setResult(res);
      if (res.checkoutUrl) {
        // Redirect to Stripe — the webhook on completion flips the request
        // to paid and starts matching.
        window.location.href = res.checkoutUrl;
        return;
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Post-submit success view
  if (result?.propertyCheck && !result.checkoutUrl) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper elevation={1} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          <VerifierMatchingStatus
            requestId={result.propertyCheck.id}
            onAssigned={() => {}}
            onCancelled={() => {}}
            onRebook={() => {
              setResult(null);
              setError(null);
              dispatch({ type: 'setStep', payload: 2 });
            }}
            onPickVerifier={() => {
              setResult(null);
              setError(null);
              dispatch({ type: 'set', payload: { selectionMode: 'direct_verifier_marketplace', requestedVerifierId: '' } });
              dispatch({ type: 'setStep', payload: 2 });
            }}
          />
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={() => navigate('/property-check/dashboard')}>
              View all my checks
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(`/property-check/c/${result.propertyCheck.id}`)}
            >
              View this check
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <BookingFrame state={state} onBack={() => navigate('/property-check')}>

        {resumedDraft && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={startFresh}>
                Start a new booking
              </Button>
            }
          >
            You're continuing an unfinished booking{state.location?.address ? ` for ${state.location.address}` : ''}.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ─── Screen 1 ─────────────────────────────────────────────── */}
        {state.step === 0 && (
          <Stack spacing={4}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Pick a package
              </Typography>
              <Grid container spacing={2}>
                {packages.map((pkg) => (
                  <Grid item xs={12} sm={6} key={pkg.slug}>
                    <PackageCard
                      pkg={pkg}
                      selected={state.packageSlug === pkg.slug}
                      onSelect={() => handleSelectPackage(pkg)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Location & details appear once a package is chosen */}
            {state.packageSlug && (
            <>
            {user && ownerProperties.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Use a saved property
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={state.ownerPropertyId || ''}
                  onChange={(e) => handleSelectOwnerProperty(e.target.value || null)}
                  helperText="Or enter a new address below"
                >
                  <MenuItem value="">
                    <em>Enter a new property</em>
                  </MenuItem>
                  {ownerProperties.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {/* Location — same component + pattern as the New Project wizard */}
            <Box ref={locationRef} sx={{ scrollMarginTop: '90px' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Location <RequiredMark />
              </Typography>
              <LocationInput
                value={state.location}
                onChange={(value) => dispatch({ type: 'set', payload: { location: value } })}
                placeholder="Enter property location"
              />
            </Box>

            {/* Description with AI Auto-Complete — same component the New
                Project wizard and CreateVerificationRequest use, extracted
                into AIDescriptionField so the pattern lives in one place. */}
            <AIDescriptionField
              value={state.description}
              onChange={(value) => dispatch({ type: 'set', payload: { description: value } })}
              label="What do you want the verifier to check?"
              placeholder="Example: Please check the roof — there was a leak in the master bedroom last winter. Also confirm the geyser is still working and the garden tap isn't leaking."
              helperText="Optional. Add anything specific the verifier should focus on — leaks, repairs to confirm, security concerns. Leave blank for a standard check."
              rows={4}
              projectType="property_check"
              taskType={state.pkg?.displayName}
              location={state.location}
              onError={setError}
            />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                When?
              </Typography>
              <FormControl>
                <RadioGroup
                  value={state.scheduling.flexibility}
                  onChange={(e) =>
                    dispatch({
                      type: 'set',
                      payload: {
                        scheduling: { ...state.scheduling, flexibility: e.target.value },
                      },
                    })
                  }
                >
                  <FormControlLabel value="flexible" control={<Radio />} label="This week — pick the best day" />
                  <FormControlLabel value="specific_date" control={<Radio />} label="A specific date" />
                  <FormControlLabel value="verifier_proposes" control={<Radio />} label="Verifier proposes a time" />
                </RadioGroup>
              </FormControl>
              {state.scheduling.flexibility === 'specific_date' && (
                <TextField
                  type="date"
                  label="Preferred date"
                  InputLabelProps={{ shrink: true }}
                  value={state.scheduling.requestedDate}
                  onChange={(e) =>
                    dispatch({
                      type: 'set',
                      payload: {
                        scheduling: { ...state.scheduling, requestedDate: e.target.value },
                      },
                    })
                  }
                  sx={{ mt: 2 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                disabled={!canAdvanceFromScreen1}
                onClick={() => dispatch({ type: 'setStep', payload: 1 })}
              >
                Next
              </Button>
            </Box>
            </>
            )}
          </Stack>
        )}

        {/* ─── Screen 2 ─────────────────────────────────────────────── */}
        {state.step === 1 && (
          <Stack spacing={4}>
            <Box>
              <PropertyAccessForm
                value={state.propertyAccess}
                onChange={(value) => dispatch({ type: 'set', payload: { propertyAccess: value } })}
              />
            </Box>
            <Divider />
            <Box>
              <FocusAreaChips
                areas={state.focusAreas}
                onChange={(areas) => dispatch({ type: 'set', payload: { focusAreas: areas } })}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => dispatch({ type: 'setStep', payload: 0 })}>Back</Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => dispatch({ type: 'setStep', payload: 2 })}
              >
                Continue to payment
              </Button>
            </Box>
          </Stack>
        )}

        {/* ─── Screen 3 ─────────────────────────────────────────────── */}
        {state.step === 2 && (
          <Stack spacing={4}>
            {!user && (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <LightweightSignupInline
                  onSuccess={async () => {
                    await refreshAuth();
                    rememberPostVerifyReturn();
                    navigate('/verify-email', {
                      state: {
                        from: {
                          pathname: '/property-check/book',
                          search: '?resume=1',
                        },
                      },
                    });
                  }}
                  onSwitchToLogin={() =>
                    navigate('/login', {
                      state: {
                        from: {
                          pathname: '/property-check/book',
                          search: '?resume=1',
                        },
                      },
                    })
                  }
                />
              </Paper>
            )}

            {user && (
              <>
                {!emailVerified && (
                  <Alert
                    severity="warning"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          rememberPostVerifyReturn();
                          navigate('/verify-email', {
                            state: {
                              from: {
                                pathname: '/property-check/book',
                                search: '?resume=1',
                              },
                            },
                          });
                        }}
                      >
                        Verify now
                      </Button>
                    }
                  >
                    Verify your email before payment. Your package, address, and check details
                    are saved here, so you can continue after entering the 6-digit code.
                  </Alert>
                )}

                <BookingSummary state={state} />

                <VerifierSelection
                  state={state}
                  previousVerifiers={previousVerifiers}
                  availableVerifiers={availableVerifiers}
                  loading={loadingVerifiers}
                  onChange={(payload) => dispatch({ type: 'set', payload })}
                />

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Payment
                  </Typography>
                  {process.env.NODE_ENV !== 'production' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Dev mode: <strong>manual_test</strong> payment will mark the booking as paid
                      without charging anything. Switch to <strong>stripe_checkout</strong> to test
                      the redirect flow.
                    </Alert>
                  )}
                  <FormControl>
                    <FormLabel sx={{ mb: 1 }}>Payment method</FormLabel>
                    <RadioGroup
                      value={state.paymentMethod}
                      onChange={(e) =>
                        dispatch({ type: 'set', payload: { paymentMethod: e.target.value } })
                      }
                    >
                      <FormControlLabel
                        value="pay_on_report"
                        control={<Radio />}
                        label={(
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Pay when the report arrives — recommended</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Nothing is charged before the visit. The verifier goes, takes dated GPS-tagged photos and writes the report; you pay to open it.
                            </Typography>
                          </Box>
                        )}
                      />
                      <FormControlLabel
                        value="stripe_checkout"
                        control={<Radio />}
                        label="Pay now by card (Stripe Checkout)"
                      />
                      {process.env.NODE_ENV !== 'production' && (
                        <FormControlLabel
                          value="manual_test"
                          control={<Radio />}
                          label="Manual test (dev only)"
                        />
                      )}
                    </RadioGroup>
                  </FormControl>

                  {state.paymentMethod === 'stripe_checkout' && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        bgcolor: colors.bg?.muted || '#f8fafc',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <LockOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'success.main', mt: '2px', flexShrink: 0 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Secure checkout.</strong> You'll be redirected to Stripe — our
                        payment processor — to complete payment, then returned here automatically.
                        Eqaya never sees or stores your card details.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button onClick={() => dispatch({ type: 'setStep', payload: 1 })}>Back</Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={
                      submitting ||
                      !emailVerified ||
                      (state.selectionMode !== 'auto_assign' && !state.requestedVerifierId)
                    }
                    sx={{ minWidth: 240 }}
                  >
                    {submitting ? (
                      <CircularProgress size={22} sx={{ color: 'inherit' }} />
                    ) : (
                      `Book and pay $${Number(state.pkg?.basePriceUsd || 0).toFixed(2)}`
                    )}
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        )}
    </BookingFrame>
  );
};

// What a check is, in the order it happens — so the list is numbered.
const HOW_IT_WORKS = [
  ['You book', 'Pick a package, give the address and say what you want looked at.'],
  ['A verified person goes', 'Someone Eqaya has ID-checked visits and checks in on site. Their phone’s GPS proves they were there.'],
  ['You get the report', 'Dated, GPS-tagged photos, a condition checklist and a plain written report of what they found.'],
];

const btnSx = {
  '& .MuiButton-root': { borderRadius: '10px', textTransform: 'none', fontWeight: 700 },
  '& .MuiButton-containedPrimary, & .MuiButton-contained': { bgcolor: brand.primaryDark, boxShadow: 'none', '&:hover': { bgcolor: '#134e4a', boxShadow: 'none' } },
  '& .MuiButton-sizeLarge': { height: 50, px: 3.5, fontSize: 16 },
  '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: brand.primaryDark },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: 700, color: INK },
};

/**
 * The page frame, matching Find a realtor / Find a conveyancer (owner,
 * 2026-09-26): a white header band with eyebrow, headline and lead, then the
 * wizard in one card on the left and a sidebar on the right that explains the
 * check and keeps a live summary of what is being booked.
 */
function BookingFrame({ state, onBack, children }) {
  const { listings } = useFeatures() || {};
  const price = state.pkg ? `$${Number(state.pkg.basePriceUsd || 0).toFixed(0)}` : null;
  const address = state.location?.address || state.location?.formattedAddress || (typeof state.location === 'string' ? state.location : null);
  return (
    <Box sx={{ bgcolor: '#f6f9f8', minHeight: '100vh' }}>
      <Seo title="Book a property check" description="A verified person visits the property and sends you dated, GPS-tagged photos and a written report." canonicalPath="/property-check/book" />
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dfe6e4' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4.5 } }}>
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1.5, ml: -1, textTransform: 'none', fontWeight: 600, color: brand.primaryDark }}>
            Property check
          </Button>
          <Stack spacing={1.25} sx={{ maxWidth: 860 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.primaryDark }}>
              Property · Book a check
            </Typography>
            <Typography component="h1" sx={{ fontSize: { xs: 32, md: 44 }, fontWeight: 800, lineHeight: 1.1, color: INK, letterSpacing: '-0.015em', textWrap: 'balance' }}>
              Have someone you can trust look at it for you
            </Typography>
            <Typography sx={{ fontSize: { xs: 16, md: 18 }, lineHeight: 1.55, color: textColors.secondary }}>
              A person Eqaya has verified visits the property and sends you dated, GPS-tagged photos and a written report. Three short screens, and you see the price before you book.
            </Typography>
          </Stack>
          <Stepper activeStep={state.step} sx={{ mt: 3.5, maxWidth: 760, ...btnSx }}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' }, gap: 4.5, alignItems: 'start' }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 0 0 1px #dfe6e4', p: { xs: 2.5, md: 4 }, minWidth: 0, ...btnSx }}>
            {children}
          </Box>
          <Stack spacing={2.25}>
            <LightCard title="Your booking">
              {state.pkg ? (
                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                    <Typography sx={{ fontWeight: 600, color: INK }}>{state.pkg.displayName}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 22, color: INK, fontVariantNumeric: 'tabular-nums' }}>{price}<Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: textColors.secondary, ml: 0.5 }}>USD</Typography></Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 14, color: textColors.secondary }}>{address || 'Address not set yet'}</Typography>
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 15, color: textColors.secondary }}>Pick a package to see the price.</Typography>
              )}
            </LightCard>
            <DarkCard title="How a check works">
              <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                {HOW_IT_WORKS.map(([title, body], i) => (
                  <Box component="li" key={title} sx={{ display: 'flex', gap: 1.5 }}>
                    <Box aria-hidden sx={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', bgcolor: '#fff', color: '#134e4a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{i + 1}</Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#fff' }}>{title}</Typography>
                      <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: '#e6f4f2' }}>{body}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 2.25, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)', display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <LockOutlinedIcon fontSize="small" sx={{ color: '#fff', mt: '2px' }} />
                <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: '#e6f4f2' }}>
                  Nothing is charged before the visit if you choose to pay when the report arrives.
                </Typography>
              </Box>
            </DarkCard>
            {listings && (
              <LightCard title="Buying the property?">
                <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: textColors.secondary, mb: 1 }}>
                  A check tells you what is on the ground. Check the people too: the agent should be registered with the Estate Agents Council, and the transfer done by a conveyancer.
                </Typography>
                <Stack spacing={0.5}>
                  <Link component={RouterLink} to="/realtors" sx={{ fontWeight: 700 }}>Find a realtor in good standing →</Link>
                  <Link component={RouterLink} to="/conveyancers" sx={{ fontWeight: 700 }}>Find a conveyancer →</Link>
                </Stack>
              </LightCard>
            )}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

const BookingSummary = ({ state }) => (
  <Box
    sx={{
      bgcolor: colors.bg?.muted || '#f8fafc',
      borderRadius: 2,
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
      Booking summary
    </Typography>
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      <Row label="Package" value={state.pkg?.displayName || '—'} />
      <Row
        label="Address"
        value={
          state.location?.address ||
          state.location?.formattedAddress ||
          (typeof state.location === 'string' ? state.location : '—')
        }
      />
      <Row
        label="Schedule"
        value={
          state.scheduling.flexibility === 'specific_date'
            ? state.scheduling.requestedDate || 'Pick a date'
            : state.scheduling.flexibility === 'verifier_proposes'
            ? 'Verifier will propose'
            : 'This week'
        }
      />
      {state.focusAreas.length > 0 && (
        <Row label="Focus" value={state.focusAreas.join(', ')} />
      )}
      <Row
        label="Verifier"
        value={state.selectionMode !== 'auto_assign' ? 'Specific verifier' : 'Auto-match'}
      />
      <Divider />
      <Row
        label="Total"
        value={`$${Number(state.pkg?.basePriceUsd || 0).toFixed(2)} USD`}
        bold
      />
    </Stack>
  </Box>
);

const verifierIdOf = (verifier) => verifier.verifier_id || verifier.user_id || verifier.id;
const verifierNameOf = (verifier) =>
  verifier.full_name ||
  verifier.name ||
  verifier.company_name ||
  [verifier.firstName, verifier.lastName].filter(Boolean).join(' ') ||
  verifier.email ||
  'Verifier';

const VerifierSelection = ({
  state,
  previousVerifiers,
  availableVerifiers,
  loading,
  onChange,
}) => {
  const rawMode = state.selectionMode || 'auto_assign';
  const mode = rawMode === 'direct_verifier' ? 'direct_verifier_marketplace' : rawMode;
  const showPrevious = mode === 'direct_verifier_previous';
  const showMarketplace = mode === 'direct_verifier_marketplace';
  const directMode = showPrevious || showMarketplace || mode === 'direct_verifier';
  const selectedPool = showPrevious ? previousVerifiers : availableVerifiers;

  const handleModeChange = (nextMode) => {
    onChange({
      selectionMode: nextMode,
      requestedVerifierId: '',
    });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Verifier
      </Typography>
      <FormControl>
        <FormLabel sx={{ mb: 1 }}>How should we assign this check?</FormLabel>
        <RadioGroup value={mode} onChange={(e) => handleModeChange(e.target.value)}>
          <FormControlLabel
            value="auto_assign"
            control={<Radio />}
            label="Post it to qualified verifiers and auto-match the first best fit"
          />
          <FormControlLabel
            value="direct_verifier_previous"
            control={<Radio />}
            label="Pick a verifier I've worked with before"
          />
          <FormControlLabel
            value="direct_verifier_marketplace"
            control={<Radio />}
            label="Choose a specific verifier"
          />
        </RadioGroup>
      </FormControl>

      {directMode && (
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading verifiers...
              </Typography>
            </Stack>
          ) : selectedPool.length === 0 ? (
            <Alert severity="info">
              {showPrevious
                ? "You don't have previous verifiers yet. Choose a specific verifier or use auto-match."
                : 'No available verifiers were found. Use auto-match to post the job to the wider pool.'}
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              label={showPrevious ? 'Previous verifier' : 'Specific verifier'}
              value={state.requestedVerifierId || ''}
              onChange={(e) =>
                onChange({
                  selectionMode: mode,
                  requestedVerifierId: e.target.value,
                })
              }
              helperText="The verifier will receive a targeted verification offer after payment."
            >
              {selectedPool.map((verifier) => {
                const id = verifierIdOf(verifier);
                return (
                  <MenuItem key={id} value={id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{verifierNameOf(verifier)}</span>
                      {showPrevious && <Chip label="Previous" size="small" />}
                    </Stack>
                  </MenuItem>
                );
              })}
            </TextField>
          )}
        </Box>
      )}
    </Box>
  );
};

const Row = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500, textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

export default BookPropertyCheck;
